import { cameraTagStyle } from '../CameraTagChip';
import type { CameraTagCatalog, CameraTagCategory } from './cameraTagsApi';

/** The camera form's PTZ setting ↔ the API's `ptz_manual` (null = detect over ONVIF). */
export type PtzSetting = 'auto' | 'yes' | 'no';
export const ptzSettingToApi = (v: PtzSetting): boolean | null => (v === 'yes' ? true : v === 'no' ? false : null);
export const ptzSettingFromApi = (v: unknown): PtzSetting => (v === true ? 'yes' : v === false ? 'no' : 'auto');

export interface CameraTagFieldsProps {
  catalog: CameraTagCatalog;
  tagIds: string[];
  onTagsChange: (ids: string[]) => void;
  ptz: PtzSetting;
  onPtzChange: (value: PtzSetting) => void;
  /** What ONVIF last reported (edit only): true/false, null = not yet; undefined hides the hint. */
  detectedPtz?: boolean | null;
}

/**
 * Tags + PTZ on the camera add/edit form, both dashboards. One row per category; a one-per-
 * camera category (Placement) behaves like radio buttons that can be cleared: picking Outdoor
 * drops Indoor, clicking Outdoor again clears it.
 */
export default function CameraTagFields({ catalog, tagIds, onTagsChange, ptz, onPtzChange, detectedPtz }: CameraTagFieldsProps) {
  const selected = new Set(tagIds || []);
  const categories = catalog.categories.filter((c) => c.tags.length > 0);

  function toggle(category: CameraTagCategory, tagId: string) {
    const next = new Set(selected);
    if (next.has(tagId)) {
      next.delete(tagId);
    } else {
      if (!category.multi_select) for (const t of category.tags) next.delete(t.id);
      next.add(tagId);
    }
    onTagsChange([...next]);
  }

  const detectedLabel = detectedPtz === true ? 'PTZ' : detectedPtz === false ? 'fixed' : null;

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div>
        <h4 className="text-[13px] font-semibold text-foreground">Tags</h4>
        <p className="text-[11px] text-muted-foreground">Used by the camera filters on the camera list.</p>
      </div>

      {categories.map((cat) => (
        <div key={cat.id}>
          <p className="mb-1 text-[12px] font-medium text-foreground">
            {cat.name}
            {!cat.multi_select && <span className="ml-1.5 font-normal text-muted-foreground">pick one</span>}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {cat.tags.map((t) => {
              const on = selected.has(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggle(cat, t.id)}
                  aria-pressed={on}
                  className={`rounded-md border px-2.5 py-1 text-[12px] font-medium transition-opacity ${on ? '' : 'opacity-60 hover:opacity-100'}`}
                  style={cameraTagStyle(t.color, on)}
                >
                  {on ? '✓ ' : ''}{t.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {categories.length === 0 && (
        <p className="text-[12px] text-muted-foreground">No tags yet — create them in Settings → Camera tags.</p>
      )}

      <div>
        <p className="mb-1 text-[12px] font-medium text-foreground">PTZ</p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={ptz}
            onChange={(e) => onPtzChange(e.target.value as PtzSetting)}
            className="rounded-lg border border-border bg-input px-2.5 py-1.5 text-[13px] text-foreground focus:border-brand focus:outline-none"
          >
            <option value="auto">Detect automatically (ONVIF)</option>
            <option value="yes">PTZ camera</option>
            <option value="no">Fixed camera</option>
          </select>
          {detectedPtz !== undefined && (
            <span className="text-[11px] text-muted-foreground">
              {detectedLabel ? `ONVIF reports: ${detectedLabel}` : 'Not detected yet'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
